import React from "react";
import { IconButton } from "@mui/material";
import { 
    Phone, 
    Email, 
    LocationOn, 
    Instagram, 
    Facebook, 
    WhatsApp, 
    X as XIcon, 
    Twitter 
} from "@mui/icons-material";
import { Link } from "react-router-dom";

// Custom TikTok SVG Icon component
const TikTokIcon = ({ size = 20, color = "#ffffff" }: { size?: number; color?: string }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill={color}
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M19.589 6.686a4.793 4.793 0 0 1-3.77-4.245V2h-3.445v13.672a2.896 2.896 0 0 1-5.201 1.743 2.895 2.895 0 0 1 2.313-4.636c.319 0 .628.052.918.148V9.412a6.328 6.328 0 0 0-.918-.067 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 10.82 4.48 6.29 6.29 0 0 0 1.865-4.48V8.718a8.19 8.19 0 0 0 4.758 1.513V6.786a4.836 4.836 0 0 1-1.008-.100z" />
    </svg>
);

const Footer = () => {
    // Standard WhatsApp Click-to-Chat API link with encoded greeting message
    const whatsappUrl = "https://wa.me/254720380744?text=Hello%20Power%20Tools,%20I%20have%20an%20inquiry.";

    // Fallback for X icon in case `@mui/icons-material` version uses `Twitter`
    const BrandXIcon = XIcon || Twitter;

    return (
        <footer className="bg-slate-900 text-gray-300 pt-12 pb-6 border-t-4 border-[#DE8F07]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">

                    {/* Brand & Socials Section */}
                    <div>
                        <h3 className="text-xl font-bold text-white mb-4 tracking-wide">
                            POWER <span className="text-[#DE8F07]">TOOLS</span>
                        </h3>
                        <p className="text-sm text-gray-400 leading-relaxed mb-4">
                            Your trusted destination for quality products, reliable services, and seamless shopping experiences.
                        </p>

                        {/* Social Media Links */}
                        <div className="flex flex-wrap gap-3">
                            <a
                                href="https://www.instagram.com/powertoolsenterprises"
                                target="_blank"
                                rel="noreferrer"
                                aria-label="Instagram"
                            >
                                <IconButton
                                    className="bg-slate-800 text-white hover:text-white hover:bg-[#DE8F07] transition-all duration-200"
                                    size="small"
                                >
                                    <Instagram sx={{ fontSize: 20, color: '#ffffff' }} />
                                </IconButton>
                            </a>
                            <a
                                href="https://www.facebook.com/powertoolenterprises"
                                target="_blank"
                                rel="noreferrer"
                                aria-label="Facebook"
                            >
                                <IconButton
                                    className="bg-slate-800 text-white hover:text-white hover:bg-[#DE8F07] transition-all duration-200"
                                    size="small"
                                >
                                    <Facebook sx={{ fontSize: 20, color: '#ffffff' }} />
                                </IconButton>
                            </a>
                            <a
                                href="https://x.com/"
                                target="_blank"
                                rel="noreferrer"
                                aria-label="X"
                            >
                                <IconButton
                                    className="bg-slate-800 text-white hover:text-white hover:bg-[#DE8F07] transition-all duration-200"
                                    size="small"
                                >
                                    <BrandXIcon sx={{ fontSize: 20, color: '#ffffff' }} />
                                </IconButton>
                            </a>
                            <a
                                href="https://www.tiktok.com/"
                                target="_blank"
                                rel="noreferrer"
                                aria-label="TikTok"
                            >
                                <IconButton
                                    className="bg-slate-800 text-white hover:text-white hover:bg-[#DE8F07] transition-all duration-200"
                                    size="small"
                                >
                                    <TikTokIcon size={20} color="#ffffff" />
                                </IconButton>
                            </a>
                            <a
                                href={whatsappUrl}
                                target="_blank"
                                rel="noreferrer"
                                aria-label="WhatsApp"
                            >
                                <IconButton
                                    className="bg-slate-800 text-white hover:text-white hover:bg-[#25D366] transition-all duration-200"
                                    size="small"
                                >
                                    <WhatsApp sx={{ fontSize: 20, color: '#ffffff' }} />
                                </IconButton>
                            </a>
                        </div>
                    </div>

                    {/* Quick Navigation Links */}
                    <div>
                        <h4 className="text-base font-semibold text-white mb-4 uppercase tracking-wider">
                            Quick Links
                        </h4>
                        <ul className="space-y-2 text-sm">
                            <li>
                                <Link to="/" className="hover:text-[#DE8F07] transition-colors duration-150">
                                    Home
                                </Link>
                            </li>
                            <li>
                                <Link to="/products" className="hover:text-[#DE8F07] transition-colors duration-150">
                                    Shop
                                </Link>
                            </li>
                            <li>
                                <Link to="/account/orders" className="hover:text-[#DE8F07] transition-colors duration-150">
                                    My Account
                                </Link>
                            </li>
                            <li>
                                <Link to="/cart" className="hover:text-[#DE8F07] transition-colors duration-150">
                                    Cart
                                </Link>
                            </li>
                            <li>
                                <Link to="/wishlist" className="hover:text-[#DE8F07] transition-colors duration-150">
                                    Wishlist
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Customer Support */}
                    <div>
                        <h4 className="text-base font-semibold text-white mb-4 uppercase tracking-wider">
                            Customer Support
                        </h4>
                        <ul className="space-y-2 text-sm">
                            <li>
                                <Link to="/help" className="hover:text-[#DE8F07] transition-colors duration-150">
                                    Help Center / FAQs
                                </Link>
                            </li>
                            <li>
                                <Link to="/shipping" className="hover:text-[#DE8F07] transition-colors duration-150">
                                    Shipping Policy
                                </Link>
                            </li>
                            <li>
                                <Link to="/returns" className="hover:text-[#DE8F07] transition-colors duration-150">
                                    Returns & Exchanges
                                </Link>
                            </li>
                            <li>
                                <Link to="/terms" className="hover:text-[#DE8F07] transition-colors duration-150">
                                    Terms & Conditions
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Contact Info */}
                    <div>
                        <h4 className="text-base font-semibold text-white mb-4 uppercase tracking-wider">
                            Contact Us
                        </h4>
                        <ul className="space-y-3 text-sm">
                            <li className="flex items-start gap-3">
                                <Phone sx={{ fontSize: 18, color: '#DE8F07' }} className="mt-0.5" />
                                <div>
                                    <span className="block text-xs text-gray-400">Hotline</span>
                                    <a href="tel:0720380744" className="font-medium hover:text-[#DE8F07]">
                                        0720 380 744
                                    </a>
                                </div>
                            </li>
                            <li className="flex items-start gap-3">
                                <WhatsApp sx={{ fontSize: 18, color: '#25D366' }} className="mt-0.5" />
                                <div>
                                    <span className="block text-xs text-gray-400">WhatsApp Chat</span>
                                    <a
                                        href={whatsappUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="font-medium hover:text-[#25D366]"
                                    >
                                        Chat on WhatsApp
                                    </a>
                                </div>
                            </li>
                            <li className="flex items-start gap-3">
                                <Email sx={{ fontSize: 18, color: '#DE8F07' }} className="mt-0.5" />
                                <div>
                                    <span className="block text-xs text-gray-400">Email</span>
                                    <a href="mailto:info@powertoolsltd.co.ke" className="font-medium hover:text-[#DE8F07]">
                                        info@powertoolsltd.co.ke
                                    </a>
                                </div>
                            </li>
                            <li className="flex items-start gap-3">
                                <LocationOn sx={{ fontSize: 18, color: '#DE8F07' }} className="mt-0.5" />
                                <span className="text-sm">
                                    Nairobi, Kenya
                                </span>
                            </li>
                        </ul>
                    </div>

                </div>

                {/* Divider & Copyright */}
                <div className="border-t border-slate-800 pt-6 mt-6 text-center text-xs text-gray-500">
                    <p>
                        &copy; {new Date().getFullYear()} Cyphercom Experts. All rights reserved.
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;